import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const formatCurrency = (val) => Number(val || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

const PAYMENT_OPTIONS = [
  { value: 'stripe', label: 'Pay on platform', desc: 'Set up card or bank (PAD) payments through Rentual. Autopay available.' },
  { value: 'e_transfer', label: 'e-Transfer', desc: 'Send rent via Interac e-Transfer. Your landlord marks it received.' },
  { value: 'cash', label: 'Cash', desc: 'Pay cash in person. Your landlord marks it received.' },
  { value: 'cheque', label: 'Cheque', desc: 'Pay by cheque. Your landlord marks it received.' },
  { value: 'other', label: 'Other', desc: 'Another arrangement with your landlord.' },
];

function TenantInviteAccept() {
  const { user, authReady } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [success, setSuccess] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const loadInvite = async () => {
      if (!token) {
        setError('Invite token missing.');
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/tenant-invites/validate?token=${token}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Invite not found.');
        setInvite(data.invite || null);
      } catch (err) {
        setError(err.message || 'Invite not found.');
      } finally {
        setLoading(false);
      }
    };

    loadInvite();
  }, [token]);

  useEffect(() => {
    if (!authReady) return;
    if (!user && token) {
      navigate(`/register?type=tenant&redirect=${encodeURIComponent(`/tenant/invite/accept?token=${token}`)}`);
    }
  }, [authReady, user, token, navigate]);

  const handleAccept = async () => {
    if (!user?.token || !token) return;
    if (!paymentType) {
      setError('Please select how you want to pay rent.');
      return;
    }
    setAccepting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenant-invites/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ token, payment_type: paymentType })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to accept invite.');
      setSuccess('Invite accepted! Your rent schedule is now active.');
    } catch (err) {
      setError(err.message || 'Unable to accept invite.');
    } finally {
      setAccepting(false);
    }
  };

  if (!authReady || loading) return <main className="App-main">Loading invite…</main>;

  if (error && !invite) {
    return (
      <main className="App-main">
        <div style={{ maxWidth: 520, margin: '3rem auto', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
          <h2>Unable to load invite</h2>
          <p className="status-text">{error}</p>
        </div>
      </main>
    );
  }

  if (!invite) {
    return (
      <main className="App-main">
        <p className="status-text" style={{ textAlign: 'center', marginTop: '3rem' }}>Invite not found.</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="App-main">
        <div className="dashboard-section" style={{ maxWidth: '520px', margin: '2rem auto' }}>
          <h2>Accept your tenant invite</h2>
          <p className="status-text">Log in or create an account to accept this invite.</p>
          <div className="form-actions" style={{ marginTop: '0.75rem' }}>
            <Link className="primary-button" to={`/login?type=tenant&redirect=${encodeURIComponent(`/tenant/invite/accept?token=${token}`)}`}>Log in</Link>
            <Link className="secondary-button" to={`/register?type=tenant&redirect=${encodeURIComponent(`/tenant/invite/accept?token=${token}`)}`}>Create account</Link>
          </div>
        </div>
      </main>
    );
  }

  if (user.userType !== 'tenant') {
    return (
      <main className="App-main">
        <p className="error-message" style={{ textAlign: 'center', marginTop: '3rem' }}>This invite is for a tenant account. Please log in as a tenant.</p>
      </main>
    );
  }

  if (success) {
    return (
      <main className="App-main">
        <div style={{ maxWidth: 520, margin: '3rem auto', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
          <h2>You're all set!</h2>
          <p className="status-text" style={{ lineHeight: 1.6 }}>{success}</p>
          {paymentType === 'stripe' && (
            <p className="status-text" style={{ marginTop: '0.5rem' }}>
              Head to Payments to set up your card or bank account for autopay.
            </p>
          )}
          <div className="form-actions" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
            {paymentType === 'stripe' && (
              <Link className="primary-button" to="/tenant/payments">Set up payments</Link>
            )}
            <Link className={paymentType === 'stripe' ? 'secondary-button' : 'primary-button'} to="/tenant/dashboard">Go to dashboard</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="App-main">
      <div style={{ maxWidth: 580, margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <p className="eyebrow-tag">Tenant invite</p>
          <h2 style={{ marginBottom: '0.25rem' }}>Confirm your rent details</h2>
          <p className="status-text">Review the details below, choose how you want to pay, and accept.</p>
        </div>

        {/* Invite details */}
        <div style={{
          padding: '1.25rem 1.5rem', borderRadius: '12px',
          border: '1px solid var(--border-color, #e5e7eb)', marginBottom: '1.25rem'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <p style={{ fontSize: '0.78rem', color: 'var(--light-text-color)', fontWeight: 500, marginBottom: '0.15rem' }}>Property</p>
              <p style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>{invite.property_address || '—'}</p>
            </div>
            <div>
              <p style={{ fontSize: '0.78rem', color: 'var(--light-text-color)', fontWeight: 500, marginBottom: '0.15rem' }}>Landlord</p>
              <p style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>{invite.landlord_email || '—'}</p>
            </div>
            <div>
              <p style={{ fontSize: '0.78rem', color: 'var(--light-text-color)', fontWeight: 500, marginBottom: '0.15rem' }}>Monthly rent</p>
              <p style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>{formatCurrency(invite.monthly_rent)}</p>
            </div>
            <div>
              <p style={{ fontSize: '0.78rem', color: 'var(--light-text-color)', fontWeight: 500, marginBottom: '0.15rem' }}>Due day</p>
              <p style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>Day {invite.rent_due_day} of each month</p>
            </div>
            <div>
              <p style={{ fontSize: '0.78rem', color: 'var(--light-text-color)', fontWeight: 500, marginBottom: '0.15rem' }}>Lease start</p>
              <p style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>{invite.lease_start_date || '—'}</p>
            </div>
            {invite.lease_end_date && (
              <div>
                <p style={{ fontSize: '0.78rem', color: 'var(--light-text-color)', fontWeight: 500, marginBottom: '0.15rem' }}>Lease end</p>
                <p style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>{invite.lease_end_date}</p>
              </div>
            )}
          </div>
          {invite.message && (
            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
              <p style={{ fontSize: '0.78rem', color: 'var(--light-text-color)', fontWeight: 500, marginBottom: '0.25rem' }}>Message from landlord</p>
              <p style={{ fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>{invite.message}</p>
            </div>
          )}
        </div>

        {/* Payment method selection */}
        <div style={{
          padding: '1.25rem 1.5rem', borderRadius: '12px',
          border: '1px solid var(--border-color, #e5e7eb)', marginBottom: '1.25rem'
        }}>
          <p style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 0.25rem' }}>How would you like to pay rent?</p>
          <p className="status-text" style={{ margin: '0 0 1rem', fontSize: '0.82rem' }}>You can change this later in your payment settings.</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {PAYMENT_OPTIONS.map((opt) => {
              const selected = paymentType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setPaymentType(opt.value); setError(''); }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                    padding: '0.75rem 1rem', borderRadius: '10px', cursor: 'pointer',
                    textAlign: 'left', width: '100%',
                    border: selected ? '2px solid var(--accent-color, #28b6c8)' : '1px solid var(--border-color, #e5e7eb)',
                    background: selected ? 'rgba(40,182,200,0.06)' : 'var(--card-bg, var(--surface-color, transparent))',
                    color: 'var(--text-color)',
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                    border: selected ? '6px solid var(--accent-color, #28b6c8)' : '2px solid var(--border-color, #ccc)',
                    background: 'var(--card-bg, var(--background-color, #fff))',
                    transition: 'all 0.15s',
                  }} />
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem' }}>{opt.label}</p>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.78rem', color: 'var(--light-text-color)' }}>{opt.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {error && <p className="error-message">{error}</p>}

        <button
          className="primary-button"
          onClick={handleAccept}
          disabled={accepting || !paymentType}
          style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}
        >
          {accepting ? 'Accepting…' : !paymentType ? 'Select payment method to continue' : 'Accept invite'}
        </button>

        <p className="status-text" style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.78rem' }}>
          By accepting, you agree to the rent terms above. You can update your payment method anytime.
        </p>
      </div>
    </main>
  );
}

export default TenantInviteAccept;
